import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@artstorefronts/ui';
import { Plus, X, Tag } from 'lucide-react';
import { useMutation } from '@apollo/client';
import { toast } from 'sonner';

import { CREATE_MOCK_FILTER, DELETE_MOCK_FILTER } from '../graphql/mutations';

interface MockFilter {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface FiltersManagementSectionProps {
  filtersData: { mockFilters: MockFilter[] } | undefined;
  refetchFilters: () => void;
}

function FiltersManagementSection({ filtersData, refetchFilters }: FiltersManagementSectionProps) {
  const { t } = useTranslation('artwork');
  const { t: tc } = useTranslation('common');
  const [newFilterName, setNewFilterName] = useState('');
  const [isCreateFilterOpen, setIsCreateFilterOpen] = useState(false);

  const [createMockFilter] = useMutation(CREATE_MOCK_FILTER);
  const [deleteMockFilter] = useMutation(DELETE_MOCK_FILTER);

  const handleCreateFilter = async () => {
    if (!newFilterName.trim()) {
      toast.error(t('filters.error.name-required'));
      return;
    }

    try {
      await createMockFilter({
        variables: {
          input: { name: newFilterName.trim() },
        },
      });

      toast.success(t('filters.success.created'));
      setNewFilterName('');
      setIsCreateFilterOpen(false);
      await refetchFilters();
    } catch (error) {
      console.error('Error creating filter:', error);
      toast.error(t('filters.error.create-failed'));
    }
  };

  const handleDeleteFilter = async (filterId: string) => {
    try {
      await deleteMockFilter({
        variables: {
          input: { id: filterId },
        },
      });

      toast.success(t('filters.success.deleted'));
      await refetchFilters();
    } catch (error) {
      console.error('Error deleting filter:', error);
      toast.error(t('filters.error.delete-failed'));
    }
  };

  return (
    <div className="mb-4 p-3 bg-white rounded-lg border">
      <div className="mb-2">
        <h3 className="text-lg font-semibold">{t('filters.title')}</h3>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {filtersData?.mockFilters?.map((filter: MockFilter) => (
          <div key={filter.id} className="flex items-center gap-2 bg-white px-3 py-2 rounded-md border">
            <Tag className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium">{filter.name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteFilter(filter.id)}
              className="h-5 w-5 p-0 text-black hover:text-gray-700 hover:bg-gray-100"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        ))}

        <Dialog open={isCreateFilterOpen} onOpenChange={setIsCreateFilterOpen}>
          <DialogTrigger asChild>
            <Button size="lg" className="flex items-center">
              <Plus className="size-4" />
              <span className="text-sm font-medium">{t('filters.add-filter')}</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('filters.create-dialog.title')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">{t('filters.create-dialog.name-label')}</label>
                <Input
                  value={newFilterName}
                  onChange={(e) => setNewFilterName(e.target.value)}
                  placeholder={t('filters.create-dialog.name-placeholder')}
                  onKeyPress={(e) => e.key === 'Enter' && handleCreateFilter()}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsCreateFilterOpen(false)}>
                  {tc('cancel')}
                </Button>
                <Button onClick={handleCreateFilter}>{t('filters.create-dialog.submit')}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {(!filtersData?.mockFilters || filtersData.mockFilters.length === 0) && (
          <p className="text-sm text-gray-500">{t('filters.empty-state')}</p>
        )}
      </div>
    </div>
  );
}

export default FiltersManagementSection;
